import { type TemplateKey, TEMPLATES } from "./templates";

export type Confidence = "low" | "medium" | "high";

export interface Detection {
  templateKey: TemplateKey;
  confidence: Confidence;
  scores: Record<TemplateKey, number>;
}

export interface ExtractedComponents {
  subject: string;
  action: string;
  location: string;
  element: string;
}

export interface ParsedText extends ExtractedComponents, Detection {}

const TEMPLATE_KEYS = Object.keys(TEMPLATES) as TemplateKey[];

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function resolveTie(key1: TemplateKey, key2: TemplateKey, text: string): TemplateKey {
  if (/\b(inside|interior|indoors|room|office)\b/.test(text)) {
    return TEMPLATES[key1].setting === "indoor" ? key1 : key2;
  }
  if (/\b(speed|fast|rush|motion|blur|velocity)\b/.test(text)) {
    if (key1.includes("kinetic")) {
      return key1;
    }
    if (key2.includes("kinetic")) {
      return key2;
    }
  }
  if (/\b(portrait|face|close-up|candid)\b/.test(text)) {
    if (key1 === "humanist-portrait") {
      return key1;
    }
    if (key2 === "humanist-portrait") {
      return key2;
    }
  }
  return key1;
}

export function detectTemplate(text: string): Detection {
  const normalized = text.toLowerCase();
  const scores = {} as Record<TemplateKey, number>;

  for (const key of TEMPLATE_KEYS) {
    let score = 0;
    for (const keyword of TEMPLATES[key].keywords) {
      const regex = new RegExp(`\\b${escapeRegex(keyword)}\\b`, "i");
      if (regex.test(normalized)) {
        score += 1;
      }
    }
    scores[key] = score;
  }

  const sorted = (Object.entries(scores) as [TemplateKey, number][]).sort((a, b) => b[1] - a[1]);
  const [first, second] = sorted;
  if (!first) {
    return { templateKey: "humanist-portrait", confidence: "low", scores };
  }

  if (first[1] === 0) {
    return { templateKey: "humanist-portrait", confidence: "low", scores };
  }

  if (second && first[1] === second[1]) {
    return {
      templateKey: resolveTie(first[0], second[0], normalized),
      confidence: "medium",
      scores,
    };
  }

  return {
    templateKey: first[0],
    confidence: first[1] >= 3 ? "high" : "medium",
    scores,
  };
}

function extractElement(text: string): string {
  const patterns = [
    /(?:during|at|in)\s+(golden hour|blue hour|sunset|sunrise|dawn|dusk|twilight|midday|noon)/i,
    /(?:with|amid|through)\s+(rain|fog|mist|dust|snow|haze|steam|smoke|wind)/i,
    /(?:under|beneath|in)\s+(neon lights?|streetlights?|fluorescent light|natural light|candlelight)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }
  return "";
}

export function extractComponents(text: string): ExtractedComponents {
  const result: ExtractedComponents = { subject: "", action: "", location: "", element: "" };

  let cleaned = text.trim();

  // Strip common preambles
  cleaned = cleaned.replace(
    /^(?:i want |i need |create |generate |make |shoot |capture |photograph |a (?:photo|image|shot|picture) of )/i,
    "",
  );

  // Pattern A: [subject] [verb-ing] [preposition] [location]
  const patternA =
    /^((?:(?:a|an|the)\s+)?[\w\s-]+?)\s+([\w-]+ing(?:\s+[\w-]+)?)\s+(?:through|across|along|in|at|on|into|over|around|within|near|by|overlooking)\s+(.+)$/i;

  // Pattern C: [subject] in/at [location] [verb-ing]
  const patternC = /^((?:(?:a|an|the)\s+)?[\w\s-]+?)\s+(?:in|at)\s+([\w\s,-]+?)\s+([\w-]+ing.*)$/i;

  // Pattern B: [subject] [verb-ing] [rest]
  const patternB = /^((?:(?:a|an|the)\s+)?[\w\s-]+?)\s+([\w-]+ing(?:\s+[\w-]+)*)\s*(.*)$/i;

  let match: RegExpMatchArray | null;

  if ((match = cleaned.match(patternA))) {
    result.subject = (match[1] ?? "").trim();
    result.action = (match[2] ?? "").trim();
    result.location = (match[3] ?? "").trim();
  } else if ((match = cleaned.match(patternC))) {
    result.subject = (match[1] ?? "").trim();
    result.location = (match[2] ?? "").trim();
    result.action = (match[3] ?? "").trim();
  } else if ((match = cleaned.match(patternB))) {
    result.subject = (match[1] ?? "").trim();
    result.action = (match[2] ?? "").trim();
    if (match[3]) {
      result.location = (match[3] ?? "").trim();
    }
  } else {
    result.subject = cleaned;
  }

  result.element = extractElement(text);

  return result;
}

export function parse(text: string): ParsedText {
  return { ...extractComponents(text), ...detectTemplate(text) };
}
